import { ECustomerEvents, ESessionEvents, IAnalyticsCustomerData, IAnalyticsSessionData } from 'data/analytics';
import { getLinks, IPublicSession } from 'state/session';
import { UAParser } from 'ua-parser-js';
import { sendMetric } from 'utils/network/postAnalytics';
import { user } from 'utils/auth/user';
import { IAnalyticsDto } from 'state/analytics';
import { resetVoipQuality, voipQualitySnapshot } from 'state/session/voipQuality';
import { t } from '@lingui/core/macro';

export async function sendCustomerAnalyticsEvent(
  action: ECustomerEvents,
  session: IPublicSession,
  analyticsData?: IAnalyticsDto
) {
  if (!session.id) return;
  const { browser, device, os } = UAParser(navigator.userAgent);
  const data: IAnalyticsCustomerData = {
    sessionId: session.id!,
    mmToken: session.aveSessionId!,
    voipUsed: analyticsData?.voipUsed ?? false,
    action,
    deviceType: device.type ?? '',
    deviceBrand: device.vendor ?? '',
    browserName: browser.name ?? '',
    browserVersion: browser.version ?? '',
    osName: os.name ?? '',
    screenWidth: window.innerWidth.toString(),
    screenHeight: window.innerHeight.toString(),
    timestamp: String(Date.now()),
  };
  await sendMetric<IAnalyticsCustomerData>('customer', data);
}

export async function sendSessionAnalyticsEvent(
  type: ESessionEvents,
  session: IPublicSession,
  sendBeacon?: boolean,
  analyticsData?: IAnalyticsDto
) {
  if (!session.id) return;

  // AVS-4419: the counters describe the session that just ended, so the snapshot (read into the
  // payload below) and the reset must happen here, synchronously, before anything is awaited —
  // including the lazy `import` a few lines down. That import is a real await: on the SESSION_ENDED
  // call from `state/session.ts` (the only caller that omits `analyticsData`), the state module has
  // already emitted `session:end` and stopped `voipStatsSampler` by the time we get here, but
  // deferring the snapshot/reset past the import would still let anything else that runs in the
  // microtask gap observe stale or already-reset counters.
  const voipQuality = voipQualitySnapshot();
  if (type === ESessionEvents.SESSION_ENDED) {
    resetVoipQuality();
  }

  if (!analyticsData) {
    // Lazy load to avoid circular dependency when calling from the session state
    const { analyticsState } = await import('state/analytics');
    analyticsData = analyticsState().get() as IAnalyticsDto;
  }

  const dealerKvps = user.dealerId;
  const { customerStreamUrl } = getLinks();

  // Check if the active vehicle is a VTP vehicle
  const activeVehicle = session.vehicles?.[session.activeVehicleId ?? ''];
  const isVtp = activeVehicle?.isVtp === true;

  const data: IAnalyticsSessionData = {
    sessionId: session.id!,
    status: type,
    virtualExhibitionType: session.isShared ? (session.sharingType ?? '') : '', // we only track the sharing type for shared sessions
    dealerIdMat: user.dealerId ?? '',
    dealerIdKvps: dealerKvps,
    prString: session.prString ?? '',
    country: session.market!,
    voipUsed: analyticsData?.voipUsed ?? false,
    timezone: session.locale!,
    mmToken: session.aveSessionId!,
    url: customerStreamUrl,
    carSelectionMethod: session.carSelectionMethod ?? '',
    // if the car is configured via DCC/SCC, the carDisplayName holds a translated placeholder ("Configured car").
    // In that case we send an empty string so analytics can detect the absence of a real model name.
    // For empty carModel values, the analytics team has a mechanism to figure out the model name using the prCode (part of the prString)
    carModel: session.carDisplayName && session.carDisplayName !== t`Configured car` ? session.carDisplayName : '',
    dgaGeneratedAt: analyticsData?.dgaGeneratedAt ?? '',
    dealerToolUsed: analyticsData?.dealerToolUsed ?? '',
    hasTimedOut: session.hasTimedOut ?? false,
    tenant: session.partnerToken!,
    experience2DUsed: analyticsData?.experience2DUsed ?? false,
    experience3DUsed: analyticsData?.experience3DUsed ?? false,
    comparisonUsed: analyticsData?.comparisonUsed ?? false,
    isVtp,
    // AVS-4419: numbers only — see `state/session/voipQuality.ts` for what each one means.
    voipPeakParticipants: voipQuality.peakParticipants,
    voipPeersWrittenOff: voipQuality.peersWrittenOff,
    voipIceRestarts: voipQuality.iceRestarts,
    voipIceRestartGiveUps: voipQuality.iceRestartGiveUps,
    voipTransportRebuilds: voipQuality.transportRebuilds,
    voipRelayOnlyPeers: voipQuality.relayOnlyPeers,
    voipMaxObservedRttMs: voipQuality.maxObservedRttMs,
    voipPeersWithZeroFramesDecoded: voipQuality.peersWithZeroFramesDecoded,
    voipStatsSamples: voipQuality.statsSamples,
    // AVS-4460: a customer in this session could not get 3D (capacity cutoff).
    customer3DUnavailable: analyticsData?.customer3DUnavailable ?? false,
    timestamp: String(Date.now()),
  };

  await sendMetric<IAnalyticsSessionData>('session', data, sendBeacon);
}
